import { useState, useEffect, useCallback } from 'react';
import { hospitalApi } from '../../hospitalApi';

const C = '#1b6ca8';
const DARK = '#0f4c75';
const LIGHT = '#e8f4fd';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const EMPTY = {
  name: '', specialization: '', phone: '', email: '',
  licenseNo: '', availabilityDays: '', consultFee: '', status: 'active',
};

const inp = { className: 'form-control' };

function F({ label, children }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

export default function HospitalDoctors({ role }) {
  const [tab, setTab]         = useState('list');
  const [doctors, setDoctors] = useState([]);
  const [form, setForm]       = useState(EMPTY);
  const [editId, setEditId]   = useState(null);
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState('');
  const [selDays, setSelDays] = useState([]);

  const load = useCallback(() => {
    hospitalApi.getDoctors().then(d => { if (d) setDoctors(d); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm(EMPTY);
    setEditId(null);
    setSelDays([]);
    setErr('');
  };

  const startEdit = (doc) => {
    setForm({
      name: doc.name || '', specialization: doc.specialization || '',
      phone: doc.phone || '', email: doc.email || '',
      licenseNo: doc.license_no || '', availabilityDays: doc.availability_days || '',
      consultFee: doc.consult_fee != null ? String(doc.consult_fee) : '',
      status: doc.status || 'active',
    });
    setSelDays((doc.availability_days || '').split(',').map(d => d.trim()).filter(Boolean));
    setEditId(doc.id);
    setTab('manage');
    setErr('');
  };

  const toggleDay = (day) => {
    setSelDays(prev => {
      const next = prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day];
      setForm(f => ({ ...f, availabilityDays: next.join(',') }));
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setErr('Name is required'); return; }
    setSaving(true); setErr('');
    try {
      const payload = { ...form, consultFee: parseFloat(form.consultFee) || 0 };
      if (editId) await hospitalApi.updateDoctor(editId, payload);
      else await hospitalApi.createDoctor(payload);
      load();
      resetForm();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this doctor?')) return;
    await hospitalApi.deleteDoctor(id);
    load();
  };

  return (
    <div>
      <div className="tabs" style={{ marginBottom: 16 }}>
        {[
          { key: 'list', label: '👨‍⚕️ Doctors List' },
          { key: 'manage', label: role === 'admin' ? '➕ Manage Doctors' : '🔒 Manage (Admin)' },
        ].map(t => (
          <button key={t.key} className={`tab${tab === t.key ? ' active' : ''}`}
            onClick={() => { setTab(t.key); if (t.key === 'manage') resetForm(); }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Doctors List ── */}
      {tab === 'list' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {doctors.length === 0 && (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 32, color: '#9aa0a6' }}>
              No doctors found. Add doctors in the Manage tab.
            </div>
          )}
          {doctors.map(doc => (
            <div key={doc.id} style={{ background: '#fff', borderRadius: 12, padding: 18,
              boxShadow: '0 1px 6px rgba(0,0,0,.08)', borderTop: `4px solid ${doc.status === 'active' ? C : '#ccc'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: DARK }}>Dr. {doc.name}</div>
                  {doc.specialization && (
                    <div style={{ fontSize: 12, color: C, fontWeight: 600, marginTop: 2 }}>{doc.specialization}</div>
                  )}
                </div>
                <span style={{
                  background: doc.status === 'active' ? '#e8f4fd' : '#f5f5f5',
                  color: doc.status === 'active' ? C : '#888',
                  borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700
                }}>
                  {doc.status}
                </span>
              </div>
              {doc.phone && <div style={{ fontSize: 13, color: '#5f6368', marginBottom: 3 }}>📞 {doc.phone}</div>}
              {doc.availability_days && (
                <div style={{ fontSize: 12, color: '#5f6368', marginBottom: 3 }}>
                  📅 {doc.availability_days}
                </div>
              )}
              {doc.consult_fee > 0 && (
                <div style={{ fontSize: 13, fontWeight: 700, color: '#27ae60', marginTop: 4 }}>
                  Consult: ₹{Number(doc.consult_fee).toLocaleString('en-IN')}
                </div>
              )}
              {role === 'admin' && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className="btn btn-primary" style={{ flex: 1, fontSize: 12, padding: '5px 10px' }}
                    onClick={() => startEdit(doc)}>Edit</button>
                  <button style={{ flex: 1, fontSize: 12, padding: '5px 10px', background: '#fce8e6',
                    color: '#c5221f', border: '1px solid #f5c6c6', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
                    onClick={() => handleDelete(doc.id)}>Deactivate</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Manage Doctors ── */}
      {tab === 'manage' && (
        <div>
          {role !== 'admin' && (
            <div style={{ padding: 24, textAlign: 'center', color: '#c5221f' }}>Admin access required</div>
          )}
          {role === 'admin' && (
            <>
              <div className="card" style={{ marginBottom: 20 }}>
                <div className="card-header">
                  <div className="card-title">{editId ? 'Edit Doctor' : 'Add New Doctor'}</div>
                  {editId && (
                    <button style={{ background: '#f1f3f4', border: 'none', borderRadius: 6,
                      padding: '5px 12px', cursor: 'pointer', fontSize: 13 }}
                      onClick={resetForm}>+ New Doctor</button>
                  )}
                </div>
                <div className="card-body">
                  {err && <div style={{ color: '#c5221f', background: '#fce8e6', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>⚠️ {err}</div>}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
                    <F label="Name *">
                      <input {...inp} value={form.name} placeholder="Doctor's full name"
                        onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                    </F>
                    <F label="Specialization">
                      <input {...inp} value={form.specialization} placeholder="e.g. Cardiology"
                        onChange={e => setForm(f => ({ ...f, specialization: e.target.value }))} />
                    </F>
                    <F label="Phone">
                      <input {...inp} value={form.phone} placeholder="+91 98765 43210"
                        onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                    </F>
                    <F label="Email">
                      <input {...inp} type="email" value={form.email}
                        onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                    </F>
                    <F label="License No">
                      <input {...inp} value={form.licenseNo}
                        onChange={e => setForm(f => ({ ...f, licenseNo: e.target.value }))} />
                    </F>
                    <F label="Consult Fee (₹)">
                      <input {...inp} type="number" min={0} value={form.consultFee}
                        onChange={e => setForm(f => ({ ...f, consultFee: e.target.value }))} />
                    </F>
                    <F label="Status">
                      <select {...inp} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </F>
                    <div className="form-group" style={{ gridColumn: '1/-1' }}>
                      <label className="form-label">Availability Days</label>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                        {DAYS.map(day => (
                          <button key={day}
                            onClick={() => toggleDay(day)}
                            style={{
                              padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                              fontWeight: 600, border: '2px solid',
                              borderColor: selDays.includes(day) ? C : '#e8eaed',
                              background: selDays.includes(day) ? LIGHT : '#fff',
                              color: selDays.includes(day) ? DARK : '#5f6368',
                            }}>
                            {day}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
                    <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving…' : editId ? '💾 Update Doctor' : '➕ Add Doctor'}
                    </button>
                    {editId && (
                      <button style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e8eaed',
                        background: '#fff', cursor: 'pointer', fontSize: 13 }}
                        onClick={resetForm}>Cancel</button>
                    )}
                  </div>
                </div>
              </div>

              {/* Doctors table */}
              <div className="card">
                <div className="card-header">
                  <div className="card-title">All Doctors ({doctors.length})</div>
                </div>
                <div className="card-body" style={{ padding: 0 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: LIGHT }}>
                        {['Name', 'Specialization', 'Phone', 'Consult Fee', 'Availability', 'Status', 'Actions'].map(h => (
                          <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: DARK, fontWeight: 700, fontSize: 12 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {doctors.map((doc, i) => (
                        <tr key={doc.id} style={{ borderTop: '1px solid #f1f3f4', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                          <td style={{ padding: '10px 12px', fontWeight: 600, color: DARK }}>Dr. {doc.name}</td>
                          <td style={{ padding: '10px 12px', color: '#5f6368' }}>{doc.specialization || '—'}</td>
                          <td style={{ padding: '10px 12px' }}>{doc.phone || '—'}</td>
                          <td style={{ padding: '10px 12px', fontWeight: 600, color: '#27ae60' }}>
                            {doc.consult_fee > 0 ? `₹${Number(doc.consult_fee).toLocaleString('en-IN')}` : '—'}
                          </td>
                          <td style={{ padding: '10px 12px', fontSize: 11, color: '#5f6368' }}>{doc.availability_days || '—'}</td>
                          <td style={{ padding: '10px 12px' }}>
                            <span style={{
                              background: doc.status === 'active' ? '#e8f4fd' : '#f5f5f5',
                              color: doc.status === 'active' ? C : '#888',
                              borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700
                            }}>{doc.status}</span>
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button style={{ fontSize: 11, padding: '3px 8px', background: LIGHT,
                                color: DARK, border: `1px solid ${C}`, borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                                onClick={() => startEdit(doc)}>Edit</button>
                              <button style={{ fontSize: 11, padding: '3px 8px', background: '#fce8e6',
                                color: '#c5221f', border: '1px solid #f5c6c6', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                                onClick={() => handleDelete(doc.id)}>Deactivate</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {doctors.length === 0 && (
                    <div style={{ padding: 24, textAlign: 'center', color: '#9aa0a6' }}>No doctors yet</div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
